import {
  clone,
  isNil
} from 'ramda'
import React,
{
  Component,
  Fragment
} from 'react'

import { Button } from '@driftt/dds'
import { ExcludeFromRecording } from '@driftt/dds.analytics'
import {
  Div,
  Footer,
  H3,
  Modal,
  NavigationHeader,
  TextLoader
} from '@driftt/tide-core'
import { tracking } from '@driftt/traffic-control'
import {
  checkFollowStatus,
  checkUnsubscribeStatus,
  followContact,
  getAllContactProperties,
  getContact,
  unsubscribeContact,
  updateContact,
} from 'api/contacts'
import {
  fetchEnrolledPlaybookId,
  unenrollFromSequence
} from 'api/playbooks'
import TypedInputs from 'components/TypedInputs'
import Config from 'config'
import { generateQueryString } from 'utils'

import DetailPane from './DetailArea'
import LeftDetailPane from './LeftPane'

import './style.css'

const TYPES_TO_INPUTS = {
  'STRING': TypedInputs.StringInput,
  'NUMERIC': TypedInputs.NumericInput,
  'DATE': TypedInputs.DateInput,
  'DATETIME': TypedInputs.DateTimeInput,
  'BOOLEAN': TypedInputs.BooleanInput
}

class ContactDetails extends Component {
  constructor(props) {
    super(props)

    this.state = {
      contactId: props.match.params.id,
      firstLoad: true,
      isSavingData: false,
      showAddDataModal: false,
      updatedContact: null,
      showEnrollInSequenceModal: false,
      isEnrolledInSequence: false,
    }
  }


  messageEventHandler = (event) => {
    const frame = document.getElementById("contact-sequence-enroll-frame")

    if (event.source !== frame && !event.data.type) {
      return
    }

    if (event.data.type === '@@driftext/hide_enroll_modal') {
      this.setState({ showEnrollInSequenceModal: false })
    }
  }

  componendWillUnmount() {
    window.removeEventListener('message', this.messageEventHandler, false)
  }

  componentDidMount() {
    this.getContact()
    window.addEventListener('message', this.messageEventHandler, false)

  }

  getContact = async () => {
    const { contactId } = this.state
    const {
      onReceiveContact,
      orgId
    } = this.props

    try {
      const { data: contact } = await getContact(contactId)
      const { data: properties } = await getAllContactProperties()
      const { data: following } = await checkFollowStatus(contactId)

      if (!isNil(contact.email)) {
        const { data: unsubscribed } = await checkUnsubscribeStatus(orgId, contact.email)
        const { data: sequenceInfo } = await fetchEnrolledPlaybookId(contact.email)

        const sequenceName = (sequenceInfo.sequenceLock && !sequenceInfo.sequenceLock.lockTTL) && sequenceInfo.playbookName

        this.setState({
          firstLoad: false,
          properties: properties,
          isFollowed: following.following,
          isUnsubscribed: unsubscribed,
          sequenceName: sequenceName
        })
      } else {
        this.setState({
          firstLoad: false,
          properties: properties,
          isFollowed: following.following,
          isUnsubscribed: false,
          sequenceName: null
        })
      }

      onReceiveContact(contact)

    } catch (e) {
      console.warn(e)
    }
  }

  updateFollowStatus = async () => {
    const { contact } = this.props
    const { isFollowed } = this.state

    tracking.trackEvent('Contact Details - Contact Followed', {
      endUserId: contact.id,
      endUserEmail: contact.email,
      isFollowing: !isFollowed
    })

    try {
      await followContact(contact.id, !isFollowed)

      this.setState(prevState => ({ isFollowed: !prevState.isFollowed }))
    } catch (e) {
      console.warn(e)
    }
  }

  updateUnsubscribeStatus = async () => {
    const { contact } = this.props

    tracking.trackEvent('Contact Details - Contact Unsubscribed', {
      endUserId: contact.id,
      endUserEmail: contact.email
    })

    try {
      await unsubscribeContact(contact.email)

      this.setState(prevState => ({ isUnsubscribed: !prevState.isUnsubscribed }), this.getContact)

    } catch (e) {
      console.warn(e)
    }
  }

  toggleAddData = (property) => {
    if (property) {
      tracking.trackEvent('Contact Details - Contact Attribute Modal Opened', {
        property: property.name,
        propertyType: property.type
      })
    }

    this.setState(prevState => ({
      showAddDataModal: !prevState.showAddDataModal,
      editProperty: property
    }))
  }

  toggleEnrollInSequence = () => {
    const { contact } = this.props
    const { email } = contact

    tracking.trackEvent('Contact Details - Sequence Enrolled', {
      endUserId: contact.id,
      endUserEmail: contact.email
    })

    const generatedQueryString = generateQueryString([encodeURIComponent(email)], 'email').join('')

    this.setState({
      showEnrollInSequenceModal: true,
      emailArray: generatedQueryString + "&source=CONTACT_DETAILS"
    })
  }

  unenrollFromSequence = async () => {
    const { contact } = this.props

    tracking.trackEvent('Contact Details - Sequence Unenrolled', {
      endUserId: contact.id,
      endUserEmail: contact.email
    })

    try {
      await unenrollFromSequence(contact.id)

      this.setState({ sequenceName: false })

    } catch (e) {
      console.warn(e)
    }
  }

  setPropertyValue = (changedValue) => {
    this.setState({ newContactValue: changedValue })
  }

  openNewConversation = () => {
    const { contact } = this.props

    tracking.trackEvent('Contact Details - Message Sent', { endUserId: contact.id })

    window.location = `${Config.APP_URL}/conversations/visitors/${contact.id}`
  }

  clearPropertyValue = () => {
    this.setPropertyValue(null)
    this.saveNewDataEntry('DELETE')
  }

  saveNewDataEntry = async (method) => {
    const {
      contact,
      showTransientNotification
    } = this.props
    const {
      newContactValue,
      editProperty
    } = this.state

    tracking.trackEvent('Contact Details - Attribute Saved', {
      property: editProperty.name,
      propertyType: editProperty.type
    })

    const updatedContact = clone(contact)

    let editPropertyNewValue = newContactValue
    if (editProperty.type === 'DATE') {
      editPropertyNewValue = String(editPropertyNewValue)
    }

    if (updatedContact[editProperty.name]) {
      updatedContact[editProperty.name] = editPropertyNewValue
    } else {
      updatedContact.attributes[editProperty.name] = editPropertyNewValue
    }

    this.setState({
      updatedContact,
      isSavingData: true
    })

    const payload = {
      action: method,
      propertyName: editProperty.name,
      propertyValue: newContactValue,
      id: contact.id
    }

    try {
      await updateContact([payload])
      showTransientNotification({
        type: 'success',
        message: 'Contact updated'
      })
      this.getContact()
    } catch (e) {
      console.warn(e)
      showTransientNotification({
        type: 'error',
        message: `Sorry, we couldn't update the contact.`
      })
      this.setState({ updatedContact: null })
    } finally {
      this.toggleAddData(null)
      this.setState({ isSavingData: false })
    }
  }

  render() {
    const {
      contact: contactFromProps,
      orgId,
      appConfig: {
        hasEnrollInSequence,
        hasSendMessageButton
      }
    } = this.props
    const {
      editProperty,
      emailArray,
      firstLoad,
      isFollowed,
      isSavingData,
      isUnsubscribed,
      properties,
      sequenceName,
      showAddDataModal,
      showEnrollInSequenceModal,
      updatedContact
    } = this.state

    const contact = {
      ...contactFromProps,
      ...updatedContact
    }

    let TypedInputBody

    if (showAddDataModal) {
      TypedInputBody = TYPES_TO_INPUTS[editProperty.type]
    }

    return (
      <Div className="contact-details--page-container">
        {!firstLoad &&
          <ExcludeFromRecording>
            <NavigationHeader title={contact.name ? contact.name : contact.email}>
              {hasSendMessageButton && <Button onClick={this.openNewConversation}>Send message</Button>}
            </NavigationHeader>
          </ExcludeFromRecording>
        }
        {!firstLoad &&
          <Div className="contact-detail-container">
            <Div className="contact-detail-left-pane">
              <LeftDetailPane
                contact={contact}
                following={isFollowed}
                updateFollowStatus={this.updateFollowStatus}
                updateUnsubscribeStatus={this.updateUnsubscribeStatus}
                unsubscribed={isUnsubscribed}
                orgId={orgId}
                onAddData={this.toggleAddData}
                onEnrollInSequence={this.toggleEnrollInSequence}
                properties={properties}
                sequenceName={sequenceName}
                unenrollFromSequence={this.unenrollFromSequence}
                hasEnrollInSequence={hasEnrollInSequence}
              />
            </Div>
            <Div className="contact-detail-right-pane">
              <DetailPane contact={contact} orgId={orgId}
                rawProperties={properties} onAddData={this.toggleAddData} />
            </Div>
            <Modal
              open={showAddDataModal}
              size="small"
              onClose={() => this.toggleAddData(null)}
              title="Edit Attribute"
              className="contact-detail--edit-attribute-modal"
            >
              {!!editProperty &&
                <Fragment>
                  <H3 className="contact-detail--edit-attribute-modal--title">Add {editProperty.displayName}</H3>
                  <TypedInputBody
                    onChange={this.setPropertyValue}
                    property={editProperty}
                    value={contact[editProperty.name] ? contact[editProperty.name] : contact.attributes[editProperty.name]}
                  />

                  <Footer className="contact-detail--edit-attribute-modal--footer">
                    <Button variant="secondary" onClick={this.clearPropertyValue}
                      loading={isSavingData} disabled={isSavingData}>Clear
                    </Button>
                    <Button onClick={() => this.saveNewDataEntry('UPDATE')}
                      loading={isSavingData} disabled={isSavingData}>Save
                    </Button>
                  </Footer>
                </Fragment>
              }
            </Modal>
          </Div>
        }
        {firstLoad &&
          <TextLoader text="Grabbing your contact details" className="contact-detail--loader" />
        }
        {showEnrollInSequenceModal &&
          <Div className="contact-sequence-enroll-wrapper">
            <Div className="contact-sequence-enroll">
              <iframe id="contact-sequence-enroll-frame" title="Enroll your sequence"
                src={`${Config.SEQUENCE_ENROLL}${emailArray}`} />
            </Div>
          </Div>
        }
      </Div>
    )
  }
}

export default ContactDetails
